import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, useMediaQuery, createTheme } from '@mui/material';
import { lightTheme, darkTheme } from '../theme';

type ThemeMode = 'light' | 'dark';
type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  currentBreakpoint: Breakpoint;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook to get current breakpoint dynamically
export const useBreakpoint = (): Breakpoint => {
  const xs = useMediaQuery('(max-width:599px)');
  const sm = useMediaQuery('(min-width:600px) and (max-width:899px)');
  const md = useMediaQuery('(min-width:900px) and (max-width:1199px)');
  const lg = useMediaQuery('(min-width:1200px) and (max-width:1799px)');
  const xl = useMediaQuery('(min-width:1800px)');

  if (xs) return 'xs';
  if (sm) return 'sm';
  if (md) return 'md';
  if (lg) return 'lg';
  return 'xl';
};

// Function to create dynamic theme based on breakpoint
const createDynamicTheme = (baseTheme: any, breakpoint: Breakpoint) => {
  const getFontSize = (variant: string, breakpoint: Breakpoint): string => {
    const sizeMap: Record<Breakpoint, Record<string, string>> = {
      xs: {
        h1: '1.75rem',
        h2: '1.5rem',
        h3: '1.25rem',
        h4: '1.125rem',
        h5: '1rem',
        h6: '0.875rem',
        body1: '0.875rem',
        body2: '0.75rem',
        button: '0.75rem',
      },
      sm: {
        h1: '2rem',
        h2: '1.75rem',
        h3: '1.5rem',
        h4: '1.25rem',
        h5: '1.125rem',
        h6: '0.9375rem',
        body1: '0.875rem',
        body2: '0.8125rem',
        button: '0.8125rem',
      },
      md: {
        h1: '2.25rem',
        h2: '1.875rem',
        h3: '1.625rem',
        h4: '1.375rem',
        h5: '1.1875rem',
        h6: '1rem',
        body1: '0.9375rem',
        body2: '0.84375rem',
        button: '0.84375rem',
      },
      lg: {
        h1: '2.5rem',
        h2: '2rem',
        h3: '1.75rem',
        h4: '1.5rem',
        h5: '1.25rem',
        h6: '1.125rem',
        body1: '1rem',
        body2: '0.875rem',
        button: '0.875rem',
      },
      xl: {
        h1: '3rem',
        h2: '2.25rem',
        h3: '2rem',
        h4: '1.75rem',
        h5: '1.375rem',
        h6: '1.25rem',
        body1: '1.0625rem',
        body2: '0.9375rem',
        button: '0.9375rem',
      },
    };

    return sizeMap[breakpoint][variant] || baseTheme.typography[variant]?.fontSize || '1rem';
  };

  const getComponentSize = (breakpoint: Breakpoint) => {
    const sizeMap: Record<Breakpoint, { buttonPadding: string; buttonBorderRadius: string; cardBorderRadius: string; dialogBorderRadius: string }> = {
      xs: { buttonPadding: '6px 12px', buttonBorderRadius: '6px', cardBorderRadius: '8px', dialogBorderRadius: '12px' },
      sm: { buttonPadding: '8px 16px', buttonBorderRadius: '8px', cardBorderRadius: '10px', dialogBorderRadius: '14px' },
      md: { buttonPadding: '8px 16px', buttonBorderRadius: '8px', cardBorderRadius: '10px', dialogBorderRadius: '14px' },
      lg: { buttonPadding: '10px 20px', buttonBorderRadius: '10px', cardBorderRadius: '14px', dialogBorderRadius: '18px' },
      xl: { buttonPadding: '12px 24px', buttonBorderRadius: '12px', cardBorderRadius: '16px', dialogBorderRadius: '20px' },
    };

    return sizeMap[breakpoint];
  };

  const componentSizes = getComponentSize(breakpoint);

  return createTheme({
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      h1: { ...baseTheme.typography.h1, fontSize: getFontSize('2.25rem', breakpoint) },
      h2: { ...baseTheme.typography.h2, fontSize: getFontSize('1.875rem', breakpoint) },
      h3: { ...baseTheme.typography.h3, fontSize: getFontSize('1.625rem', breakpoint) },
      h4: { ...baseTheme.typography.h4, fontSize: getFontSize('1.375rem', breakpoint) },
      h5: { ...baseTheme.typography.h5, fontSize: getFontSize('1.1875rem', breakpoint) },
      h6: { ...baseTheme.typography.h6, fontSize: getFontSize('1rem', breakpoint) },
      body1: { ...baseTheme.typography.body1, fontSize: getFontSize('0.9375rem', breakpoint) },
      body2: { ...baseTheme.typography.body2, fontSize: getFontSize('0.84375rem', breakpoint) },
      button: { ...baseTheme.typography.button, fontSize: getFontSize('0.84375rem', breakpoint) },
    },
    components: {
      ...baseTheme.components,
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...baseTheme.components?.MuiButton?.styleOverrides,
          root: {
            ...baseTheme.components?.MuiButton?.styleOverrides?.root,
            padding: componentSizes.buttonPadding,
            borderRadius: componentSizes.buttonBorderRadius,
          },
        },
      },
      MuiCard: {
        ...baseTheme.components?.MuiCard,
        styleOverrides: {
          ...baseTheme.components?.MuiCard?.styleOverrides,
          root: {
            ...baseTheme.components?.MuiCard?.styleOverrides?.root,
            borderRadius: componentSizes.cardBorderRadius,
          },
        },
      },
      MuiDialog: {
        ...baseTheme.components?.MuiDialog,
        styleOverrides: {
          ...baseTheme.components?.MuiDialog?.styleOverrides,
          paper: {
            ...baseTheme.components?.MuiDialog?.styleOverrides?.paper,
            borderRadius: componentSizes.dialogBorderRadius,
          },
        },
      },
    },
  });
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Separate component to use the breakpoint hook inside ThemeProvider
const ThemeProviderInner: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');
  const currentBreakpoint = useBreakpoint();

  // Load theme preference from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode;
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      setMode(savedMode);
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, []);

  // Save theme preference to localStorage when mode changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Update favicon based on theme
  useEffect(() => {
    const updateFavicon = () => {
      const favicon32 = document.getElementById('favicon-32') as HTMLLinkElement;
      const favicon16 = document.getElementById('favicon-16') as HTMLLinkElement;
      const appleTouchIcon = document.getElementById('apple-touch-icon') as HTMLLinkElement;
      const shortcutIcon = document.getElementById('shortcut-icon') as HTMLLinkElement;

      const favicon32Path = mode === 'dark' ? '/icons/favicon-32.png' : '/icons/favicon-32-light.png';
      const favicon16Path = mode === 'dark' ? '/icons/favicon-16.png' : '/icons/favicon-16-light.png';

      if (favicon32) favicon32.href = favicon32Path;
      if (favicon16) favicon16.href = favicon16Path;
      if (appleTouchIcon) appleTouchIcon.href = '/icons/apple-touch-icon.png';
      if (shortcutIcon) shortcutIcon.href = favicon32Path;
    };

    updateFavicon();
  }, [mode]);

  const toggleTheme = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  // Create dynamic theme that responds to breakpoint changes
  const baseTheme = mode === 'light' ? lightTheme : darkTheme;
  const theme = useMemo(() => createDynamicTheme(baseTheme, currentBreakpoint), [baseTheme, currentBreakpoint]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, currentBreakpoint }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <ThemeProviderInner>
      {children}
    </ThemeProviderInner>
  );
};